import React, { useEffect, useId, useRef, useState } from "react";
import { LuFileDown, LuMailCheck } from "react-icons/lu";
import { LeadRequestApiError, submitGuideRequest } from "api/lead_request/leadRequestApi";
import { PRIVACY_URL, isWorkEmail, newRequestId } from "../contact";
import Dialog from "../Dialog";
import { AlertIcon, ArrowRightIcon, LockIcon } from "../Icons";
import { FIELD_CONTROL, FIELD_LABEL, GhostButton, PrimaryButton } from "../ui";

const FIELDS = [
  { name: "first_name", label: "First name", autoComplete: "given-name", type: "text", maxLength: 80, half: true },
  { name: "last_name", label: "Last name", autoComplete: "family-name", type: "text", maxLength: 80, half: true },
  { name: "organization", label: "Organisation", autoComplete: "organization", type: "text", maxLength: 160, half: false },
  { name: "email", label: "Work email", autoComplete: "email", type: "email", maxLength: 254, half: false },
  { name: "position", label: "Position", autoComplete: "organization-title", type: "text", maxLength: 160, half: false },
] as const;

type FieldName = (typeof FIELDS)[number]["name"];
type Values = Record<FieldName, string>;

const EMPTY_VALUES: Values = { first_name: "", last_name: "", organization: "", email: "", position: "" };

// Mounted once per page. Any element marked `data-guide-download` opens the dialog, so the ERB
// resources page can offer the guide with a plain link, the way `data-lead-form` CTAs work.
export default function GuideDownload() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const upgrade = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!(event.target as Element | null)?.closest("[data-guide-download]")) return;

      event.preventDefault();
      setOpen(true);
    };

    document.addEventListener("click", upgrade);
    return () => document.removeEventListener("click", upgrade);
  }, []);

  return open ? <GuideDownloadDialog onClose={() => setOpen(false)} /> : null;
}

function GuideDownloadDialog({ onClose }: { onClose: () => void }) {
  const headingId = useId();
  const [sentTo, setSentTo] = useState("");

  return (
    <Dialog labelledBy={headingId} width="tw-w-[min(760px,calc(100vw-2rem))]" onClose={onClose}>
      <div className="tw-grid tw-grid-cols-[minmax(0,.42fr)_minmax(0,.58fr)] sm:tw-grid-cols-1">
        <aside className="tw-bg-[#10233f] tw-p-9 tw-text-white sm:tw-p-7 sm:tw-pr-16">
          <span className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-[#0068b8]">
            <LuFileDown aria-hidden className="tw-h-6 tw-w-6" />
          </span>
          <p className="tw-mt-7 tw-mb-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.15em] tw-text-[#70d9ce] sm:tw-mt-5">
            Free employer guide
          </p>
          <h2
            id={headingId}
            className="tw-mt-3 tw-mb-0 tw-text-3xl tw-font-black tw-leading-[1.08] tw-tracking-[-.04em] sm:tw-text-2xl"
          >
            Get the MIDA succession plan guide.
          </h2>
          <p className="tw-mt-4 tw-mb-0 tw-text-sm tw-leading-6 tw-text-[#b9cddd]">
            A practical guide for HR and talent teams preparing local successors, mentoring, knowledge
            transfer and continuity plans for 2027.
          </p>
          <p className="tw-mt-7 tw-mb-0 tw-flex tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-white/15 tw-pt-6 tw-text-sm tw-leading-6 tw-text-[#d9e7f1] sm:tw-hidden">
            <LockIcon aria-hidden className="tw-mt-1 tw-h-4 tw-w-4 tw-shrink-0 tw-text-[#70d9ce]" />
            FutureLab will use your details to send the guide and relevant succession-planning updates.
          </p>
        </aside>

        <div className="tw-p-9 sm:tw-p-7">
          {sentTo ? (
            <Sent email={sentTo} onDone={onClose} />
          ) : (
            <GuideRequestForm onSent={setSentTo} />
          )}
        </div>
      </div>
    </Dialog>
  );
}

function GuideRequestForm({ onSent }: { onSent: (email: string) => void }) {
  const fieldId = useId();
  const [values, setValues] = useState<Values>(EMPTY_VALUES);
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const summaryRef = useRef<HTMLDivElement>(null);
  const requestId = useRef(newRequestId());

  const showErrors = (messages: string[]) => {
    setErrors(messages);
    requestAnimationFrame(() => summaryRef.current?.focus());
  };

  // The browser's own `required` and `type="email"` checks run first; only the work-address rule
  // and the server's answer need a message of ours.
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const email = values.email.trim();
    if (!isWorkEmail(email)) {
      setEmailInvalid(true);
      showErrors(["Use your work email — we can't send the guide to a personal inbox."]);
      return;
    }

    setSubmitting(true);
    setEmailInvalid(false);
    setErrors([]);

    try {
      await submitGuideRequest({
        request_id: requestId.current,
        website: honeypot,
        guide_request: {
          ...(Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value.trim()])) as Values),
          consent,
        },
      });

      const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
      gtag?.("event", "generate_lead", { enquiry_type: "mida_guide" });

      onSent(email);
    } catch (error) {
      const messages = error instanceof LeadRequestApiError ? error.errors : [];

      setSubmitting(false);
      showErrors(messages.length ? messages : ["We couldn't send that just now. Check your connection and try again."]);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="tw-min-w-0">
      <p className="tw-m-0 tw-pr-12 tw-font-black tw-text-[#173754]">Where should we send the guide?</p>
      <p className="tw-mt-1 tw-mb-0 tw-text-sm tw-text-[#5b7188]">All fields are required.</p>

      <div
        ref={summaryRef}
        tabIndex={-1}
        role="alert"
        className={
          errors.length
            ? "tw-mt-6 tw-flex tw-items-start tw-gap-3 tw-rounded-2xl tw-border tw-border-[#e8b4b4] tw-bg-[#fdf1f1] tw-p-4 tw-text-[#93231f] focus:tw-outline-none"
            : "tw-hidden"
        }
      >
        <AlertIcon aria-hidden className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
        <ul className="tw-m-0 tw-list-none tw-space-y-1 tw-p-0 tw-text-sm tw-font-bold tw-leading-6">
          {errors.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      </div>

      <div className="tw-mt-6 tw-grid tw-grid-cols-2 tw-gap-x-5 tw-gap-y-5 xs:tw-grid-cols-1">
        {FIELDS.map(({ name, label, half, ...input }) => (
          <div key={name} className={`tw-min-w-0 ${half ? "" : "tw-col-span-2 xs:tw-col-span-1"}`}>
            <label htmlFor={`${fieldId}-${name}`} className={FIELD_LABEL}>
              {label} *
            </label>
            <input
              id={`${fieldId}-${name}`}
              required
              value={values[name]}
              aria-invalid={name === "email" && emailInvalid ? true : undefined}
              onChange={(event) => setValues((current) => ({ ...current, [name]: event.target.value }))}
              className={FIELD_CONTROL}
              {...input}
            />
          </div>
        ))}
      </div>

      {/* Honeypot: hidden from people, tempting to bots. A filled value is dropped server-side. */}
      <div aria-hidden className="tw-absolute tw-left-[-9999px] tw-h-px tw-w-px tw-overflow-hidden">
        <label htmlFor={`${fieldId}-website`}>Website</label>
        <input
          id={`${fieldId}-website`}
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(event) => setHoneypot(event.target.value)}
        />
      </div>

      <label
        htmlFor={`${fieldId}-consent`}
        className="tw-mt-6 tw-mb-0 tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-xl tw-bg-[#f4f8fc] tw-p-4"
      >
        <input
          id={`${fieldId}-consent`}
          type="checkbox"
          required
          checked={consent}
          onChange={(event) => setConsent(event.target.checked)}
          className="tw-mt-1 tw-h-4 tw-w-4 tw-shrink-0 tw-accent-[#0068b8]"
        />
        <span className="tw-text-sm tw-font-normal tw-leading-6 tw-text-[#304d68]">
          I agree to receive the guide and occasional succession-planning updates from FutureLab. See our{" "}
          <a href={PRIVACY_URL} target="_blank" rel="noreferrer" className="tw-font-bold tw-text-[#0068b8] tw-underline">
            privacy policy
          </a>
          .
        </span>
      </label>

      <PrimaryButton type="submit" size="lg" disabled={submitting} className="tw-mt-6 tw-w-full">
        {submitting ? "Sending the guide…" : "Email me the guide"}
        <ArrowRightIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
      </PrimaryButton>
    </form>
  );
}

// Focus lands on the heading so a screen reader hears that the guide is on its way.
function Sent({ email, onDone }: { email: string; onDone: () => void }) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-justify-center tw-py-6">
      <span
        aria-hidden
        className="tw-inline-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-[#e3f7f3] tw-text-[#0b7a63]"
      >
        <LuMailCheck className="tw-h-7 tw-w-7" />
      </span>
      <h3
        ref={headingRef}
        tabIndex={-1}
        className="tw-mt-6 tw-mb-0 tw-text-2xl tw-font-black tw-tracking-[-.03em] tw-text-[#10233f] focus:tw-outline-none"
      >
        Check your inbox.
      </h3>
      <p className="tw-mt-3 tw-mb-0 tw-leading-7 tw-text-[#536a82]">
        We&apos;ve sent the guide to <strong className="tw-text-[#10233f]">{email}</strong>. It can take
        a minute to arrive, so check your spam folder if you can&apos;t see it.
      </p>
      <GhostButton type="button" onClick={onDone} className="tw-mt-8 tw-self-start">
        Done
      </GhostButton>
    </div>
  );
}
